import { Pipe, PipeTransform } from '@angular/core';

import { capitalize, compact, each, first, has, isArray, isEmpty, isNil, isString, map, split } from 'lodash-es';

import { AddressEnum } from '../../enums/address';
import {
  ParsedComparableSale,
  ParsedSchoolData,
  ProfileLocation,
  PropertyProfile
} from '../../store/models/property-profile.model';
import { StandardAddress } from '../../typings/address';
import { WalkingFarmProperty } from '../../typings/walking-farm';

type AddressInputType =
  | PropertyProfile
  | ProfileLocation
  | ParsedComparableSale
  | ParsedSchoolData
  | WalkingFarmProperty
  | google.maps.GeocoderAddressComponent[]
  | undefined
  | null;

@Pipe({
  name: 'redAddress',
  standalone: false
})
export class AddressPipe implements PipeTransform {
  transform(value: AddressInputType, arg: AddressEnum = AddressEnum.Full) {
    let result = '';

    if (!isNil(value)) {
      const parsedAddress = this.parseAddress(value);

      switch (arg) {
        case AddressEnum.LineOne:
          result = compact([parsedAddress.streetFull, parsedAddress.unitType, parsedAddress.unit]).join(' ');
          break;
        case AddressEnum.LineTwo:
          result = compact([parsedAddress.city, compact([parsedAddress.state, parsedAddress.zipFull]).join(' ')]).join(
            ', '
          );
          break;
        case AddressEnum.LineOneAndCity:
          result = compact([
            compact([parsedAddress.streetFull, parsedAddress.unitType, parsedAddress.unit]).join(' '),
            parsedAddress.city
          ]).join(', ');
          break;
        case AddressEnum.ZipFull:
          result = parsedAddress.zipFull || '';
          break;
        case AddressEnum.Geocode:
          result = compact([
            parsedAddress.unit,
            parsedAddress.unitType,
            parsedAddress.streetFull,
            parsedAddress.city,
            parsedAddress.state,
            parsedAddress.zipFull,
            'USA'
          ]).join(', ');
          break;
        case AddressEnum.Zip:
          result = parsedAddress.zip || '';
          break;
        case AddressEnum.City:
          result = parsedAddress.city || '';
          break;
        default:
          result = compact([
            compact([parsedAddress.streetFull, parsedAddress.unitType, parsedAddress.unit]).join(' '),
            compact([parsedAddress.city, compact([parsedAddress.state, parsedAddress.zipFull]).join(' ')]).join(', ')
          ]).join(' ');
          break;
      }
    }

    if (isEmpty(result)) {
      result = '--';
    }

    return result;
  }

  isWalkingFarmProperty(value: AddressInputType): value is WalkingFarmProperty {
    return has(value, 'A001_Address') && isString(value?.A001_Address);
  }

  isLocation(value: AddressInputType): value is ProfileLocation {
    return has(value, 'Address') && isString(value?.Address);
  }

  isParsedComparableSale(value: AddressInputType): value is ParsedComparableSale {
    return has(value, 'data.SiteAddress');
  }

  isParsedSchool(value: AddressInputType): value is ParsedSchoolData {
    return has(value, 'SchoolAddress');
  }

  isSavedPropertyProfile(value: AddressInputType): value is PropertyProfile {
    return has(value, 'SiteAddress') && isString(value?.SiteAddress);
  }

  isGeocoderAddressComponentResult(value: AddressInputType): value is google.maps.GeocoderAddressComponent[] {
    return (
      isArray(value) && has(first(value), 'types') && has(first(value), 'short_name') && has(first(value), 'long_name')
    );
  }

  private parseAddress(value: AddressInputType) {
    const address: StandardAddress = {};

    if (this.isSavedPropertyProfile(value)) {
      this.setAddressValue(address, 'city', value.SiteCity);
      this.setAddressValue(address, 'country', 'US');
      this.setAddressValue(address, 'state', value.SiteState);
      this.setAddressValue(address, 'streetFull', value.SiteAddress);
      this.setAddressValue(address, 'unit', value.SiteUnit);
      this.setAddressValue(address, 'unitType', value.SiteUnitType);
      this.setAddressZip(address, value, 'SiteZip', 'SiteZip4');
    } else if (this.isLocation(value)) {
      this.setAddressValue(address, 'city', value.City);
      this.setAddressValue(address, 'county', value.county);
      this.setAddressValue(address, 'country', 'US');
      this.setAddressValue(address, 'state', value.State);
      this.setAddressValue(address, 'streetFull', value.Address);
      this.setAddressValue(address, 'unit', value.UnitNumber);
      this.setAddressValue(address, 'unitType', value.UnitType);
      this.setAddressZip(address, value, 'ZIP', 'ZIP4');
    } else if (this.isParsedComparableSale(value) || this.isGeocoderAddressComponentResult(value)) {
      let data: google.maps.GeocoderAddressComponent[];
      if (this.isGeocoderAddressComponentResult(value)) {
        data = value;
      } else {
        this.setAddressValue(address, 'unit', value.data.SiteUnit);
        this.setAddressValue(address, 'unitType', capitalize(value.data.SiteUnitType ?? undefined));
        data = value.addressComponents;
      }
      this.setAddressValue(address, 'city', this.getAddressComponent(data, 'locality'));
      this.setAddressValue(address, 'county', this.getAddressComponent(data, 'administrative_area_level_2'));
      this.setAddressValue(address, 'state', this.getAddressComponent(data, 'administrative_area_level_1', true));
      this.setAddressValue(address, 'country', this.getAddressComponent(data, 'country', true));
      this.setAddressValue(address, 'streetNumber', this.getAddressComponent(data, 'street_number'));
      this.setAddressValue(address, 'streetName', this.getAddressComponent(data, 'route'));
      this.setAddressValue(address, 'streetFull', compact([address.streetNumber, address.streetName]).join(' '));
      this.setAddressValue(address, 'zip', this.getAddressComponent(data, 'postal_code'));
      this.setAddressValue(address, 'zip4', this.getAddressComponent(data, 'postal_code_suffix'));
    } else if (this.isParsedSchool(value)) {
      this.setAddressValue(address, 'streetFull', value.SchoolAddress);
      this.setAddressValue(address, 'city', value.SchoolCity);
      this.setAddressValue(address, 'state', value.SchoolState);
      this.setAddressZip(address, value, 'SchoolZip', 'SchoolZip4');
    } else if (this.isWalkingFarmProperty(value)) {
      this.setAddressValue(address, 'city', value.A046_City);
      this.setAddressValue(address, 'country', 'US');
      this.setAddressValue(address, 'state', value.A047_State);
      this.setAddressValue(address, 'streetName', value.A004_StreetName);
      this.setAddressValue(address, 'streetNumber', value.A002_HouseNumber);
      this.setAddressValue(address, 'streetDirection', value.A003_StreetPrefix);
      this.setAddressValue(
        address,
        'streetFull',
        compact([address.streetNumber, address.streetDirection, address.streetName]).join(' ')
      );
      this.setAddressValue(address, 'unit', value.A005_UnitNumber);
      this.setAddressZip(address, value, 'A007_ZipCode', 'A008_Zip4');
    }
    this.setAddressValue(address, 'zipFull', compact([address.zip, address.zip4]).join('-'));
    return address;
  }

  private setAddressValue(
    address: StandardAddress,
    key: keyof StandardAddress,
    value: string | number | null | undefined
  ) {
    if (!isNil(value) && !isEmpty(`${value}`)) {
      if (['city', 'country', 'county', 'streetDirection', 'streetName', 'streetFull', 'unitType'].includes(key)) {
        address[key] = compact(map(split(`${value}`, ' '), capitalize)).join(' ');
      } else if (['zip'].includes(key)) {
        address[key] = `${value}`.replace(/^(\d{5}).*/, '$1');
      } else if (['zip4'].includes(key)) {
        address[key] = `${value}`.replace(/.*(\d{4})$/, '$1');
      } else if (['zipFull'].includes(key)) {
        address['zip'] = `${value}`.replace(/^(\d{5}).*/, '$1');
        address['zip4'] = `${value}`.replace(/.*(\d{4})$/, '$1');
        address['zipFull'] = compact([address.zip, address.zip4]).join('-');
      } else {
        address[key] = `${value}`;
      }
    }
    return address;
  }

  private getAddressComponent(components: google.maps.GeocoderAddressComponent[], key: string, shortName = false) {
    let result: string | null = null;
    each(components, (component) => {
      if (component.types.includes(key)) {
        result = shortName ? component.short_name : component.long_name;
      }
    });
    return result;
  }

  private setAddressZip(address: StandardAddress, value: AddressInputType, zipKey: string, zip4Key: string) {
    if (!isNil(value)) {
      if (has(value, zipKey) && `${value[zipKey]}`.length === 10) {
        this.setAddressValue(address, 'zipFull', `${value[zipKey]}`);
      } else if (has(value, zip4Key) && `${value[zip4Key]}`.length === 10) {
        this.setAddressValue(address, 'zipFull', `${value[zip4Key]}`);
      } else if (has(value, zipKey) && has(value, zip4Key)) {
        this.setAddressValue(address, 'zip', `${value[zipKey]}`);
        this.setAddressValue(address, 'zip4', `${value[zip4Key]}`);
      }
    }
  }
}
